import React, { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";

// Attendance on one day: who tapped "Mark present" (and when), who is on
// leave, who was marked absent automatically, who hasn't marked yet. The
// office can mark someone present for a past day, which cancels that
// day's automatic absence. Leave always wins over attendance.

const GET_ATTENDANCE_DAY = gql`
  query GetAttendanceDay($date: String!) {
    getAttendanceDay(date: $date) {
      status
      message
      day {
        date
        requiredFrom
        rows {
          employeeId
          fullName
          status
          markedAt
          markedByOffice
          note
          leaveType
          leaveMode
          autoAbsent
        }
      }
    }
  }
`;

const ADMIN_MARK_PRESENT = gql`
  mutation AdminMarkPresent($employeeId: ID!, $date: String!, $note: String) {
    adminMarkPresent(employeeId: $employeeId, date: $date, note: $note) {
      status
      message
    }
  }
`;

const STATUS = {
  PRESENT: ["Present", "bg-green-100 text-green-800"],
  LEAVE: ["On leave", "bg-blue-100 text-blue-800"],
  LEAVE_PENDING: ["Leave pending", "bg-yellow-100 text-yellow-800"],
  ABSENT: ["Absent", "bg-red-100 text-red-800"],
  NOT_MARKED: ["Not marked yet", "bg-yellow-100 text-yellow-800"],
  NOT_MARKED_PAST: ["Not marked", "bg-orange-100 text-orange-800"],
  NOT_REQUIRED: ["Not needed", "bg-gray-100 text-gray-600"],
  UPCOMING: ["Upcoming", "bg-gray-100 text-gray-600"],
  NOT_EMPLOYED: ["-", "bg-gray-100 text-gray-500"],
};

const istToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const LeaveAttendance = () => {
  const [date, setDate] = useState(istToday());
  const [filter, setFilter] = useState("ALL");
  const { data, loading, refetch } = useQuery(GET_ATTENDANCE_DAY, {
    variables: { date },
    fetchPolicy: "network-only",
  });
  const [markPresent] = useMutation(ADMIN_MARK_PRESENT);
  const res = data?.getAttendanceDay;
  const day = res?.day;
  const rows = (day?.rows || []).filter((r) => filter === "ALL" || r.status === filter);
  const count = (s) => (day?.rows || []).filter((r) => r.status === s).length;

  const mark = async (row) => {
    const note = window.prompt(
      `Mark ${row.fullName} present on ${date}?${row.autoAbsent ? " This cancels their absence and its fine." : ""}\nNote (optional):`,
      "",
    );
    if (note === null) return;
    try {
      const out = (await markPresent({ variables: { employeeId: row.employeeId, date, note: note || null } })).data
        .adminMarkPresent;
      if (out.status !== "SUCCESS") alert(out.message);
      refetch();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="text-sm text-gray-700">
          Day
          <input
            type="date"
            value={date}
            max={istToday()}
            onChange={(e) => setDate(e.target.value)}
            className="block border rounded px-2 py-1 mt-1"
          />
        </label>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="border rounded px-2 py-1 text-sm">
          <option value="ALL">Everyone</option>
          {Object.keys(STATUS)
            .filter((s) => count(s) > 0)
            .map((s) => (
              <option key={s} value={s}>
                {STATUS[s][0]} ({count(s)})
              </option>
            ))}
        </select>
        <button onClick={() => refetch()} className="text-sm text-gray-600 hover:text-gray-900 font-semibold">
          Refresh
        </button>
      </div>

      {day && !day.requiredFrom && (
        <div className="mb-3 rounded border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
          Attendance is off. Set <span className="font-mono">ATTENDANCE_REQUIRED_FROM</span> in Payouts → System
          Configurations once everyone has the app version with the Mark present button. Until then nobody is marked
          absent.
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : res?.status === "ERROR" ? (
        <p className="text-sm text-red-600">{res.message}</p>
      ) : (
        <table className="min-w-full text-left text-sm text-gray-600 border rounded">
          <thead className="bg-gray-50 text-gray-700 border-b">
            <tr>
              <th className="py-2 px-3">Professional</th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Marked at</th>
              <th className="py-2 px-3">Details</th>
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="py-6 text-center text-gray-400">
                  Nobody
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const [label, cls] = STATUS[r.status] || [r.status, "bg-gray-100"];
              const canMark = ["NOT_MARKED", "NOT_MARKED_PAST"].includes(r.status) || (r.status === "ABSENT" && r.autoAbsent);
              return (
                <tr key={r.employeeId}>
                  <td className="py-2 px-3 text-gray-800">{r.fullName}</td>
                  <td className="py-2 px-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>{label}</span>
                    {r.autoAbsent && <span className="ml-1 text-[11px] text-gray-500">auto</span>}
                  </td>
                  <td className="py-2 px-3">
                    {r.markedAt
                      ? new Date(r.markedAt).toLocaleTimeString("en-IN", {
                          timeZone: "Asia/Kolkata",
                          hour: "numeric",
                          minute: "2-digit",
                        })
                      : "—"}
                    {r.markedByOffice && <span className="ml-1 text-[11px] text-gray-500">by office</span>}
                  </td>
                  <td className="py-2 px-3 text-xs text-gray-500">
                    {r.leaveType ? `${r.leaveType} · ${r.leaveMode || ""}` : ""}
                    {r.note ? ` ${r.note}` : ""}
                  </td>
                  <td className="py-2 px-3">
                    {canMark && (
                      <button onClick={() => mark(r)} className="text-xs font-semibold text-blue-600 hover:text-blue-800">
                        Mark present
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <p className="text-xs text-gray-500 mt-3">
        Leave always wins: a day with an approved leave is a leave day even if they marked present, and approving a
        leave for a day cancels its automatic absence.
      </p>
    </div>
  );
};

export default LeaveAttendance;
